import { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FOCUS_COMMAND,
  ElementNode, // Import ElementNode
  // $getSelectionStyleValueForProperty, $patchStyleText removed as they are not in Lexical 0.3.10
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND, ListNode } from '@lexical/list';
import { $getNearestNodeOfType } from '@lexical/utils';
import { createPortal } from 'react-dom';
import {
  Bold, Italic, Underline, Strikethrough, Link, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Type, // Icons for color and font size
  Code // Added Code icon
  // Undo, Redo removed
} from 'lucide-react';

const LowPriority = COMMAND_PRIORITY_LOW;

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px'];
// These will now be CSS variables, defined in a separate CSS file.
// The actual hex values will be theme-dependent.
const FONT_COLORS = [
  'var(--lexical-color-default)',
  'var(--lexical-color-gray-1)',
  'var(--lexical-color-gray-2)',
  'var(--lexical-color-red)',
  'var(--lexical-color-orange)',
  'var(--lexical-color-yellow)',
  'var(--lexical-color-lime)',
  'var(--lexical-color-green)',
  'var(--lexical-color-teal)',
  'var(--lexical-color-cyan)',
  'var(--lexical-color-blue)',
  'var(--lexical-color-indigo)',
  'var(--lexical-color-purple)',
  'var(--lexical-color-pink)',
  // Total 14 + default = 15 colors
];
const DEFAULT_FONT_SIZE = '16px';
// This will be the CSS variable for the theme's default text color
const DEFAULT_FONT_COLOR = 'var(--lexical-color-default)';

// Helper to parse style string to object
const parseStyleText = (styleText: string): Record<string, string> => {
  const style: Record<string, string> = {};
  if (!styleText) return style;
  styleText.split(';').forEach(s => {
    const rule = s.trim();
    if (rule) {
      const [property, value] = rule.split(':').map(part => part.trim());
      if (property && value) {
        style[property] = value;
      }
    }
  });
  return style;
};

// Helper to stringify style object
const stringifyStyle = (styleObj: Record<string, string>): string => {
  return Object.entries(styleObj)
    .map(([prop, val]) => `${prop}: ${val}`)
    .join('; ') + (Object.keys(styleObj).length > 0 ? ';' : ''); // Ensure trailing semicolon if not empty
};


function getSelectedNode(selection: any) {
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isTextNode(anchorNode) ? anchorNode.getParentOrThrow() : anchorNode;
  } else {
    return $isTextNode(focusNode) ? focusNode.getParentOrThrow() : focusNode;
  }
}

interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
  title?: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ isActive, children, title, ...props }) => (
  <button
    {...props}
    title={title}
    onMouseDown={(e) => e.preventDefault()} // Prevent editor blur
    className={`p-2 m-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors
      ${isActive
        ? 'bg-blue-100 text-blue-600 dark:bg-blue-800 dark:text-blue-300'
        : 'text-gray-700 dark:text-gray-300'
      }
      disabled:opacity-50 disabled:cursor-not-allowed
    `}
  >
    {children}
  </button>
);

interface FloatingToolbarPluginProps {
  anchorElem?: HTMLElement;
  editorShellRef: React.RefObject<HTMLDivElement>; // For positioning relative to the editor area
  onExitEditingMode?: () => void; // Callback to notify parent to exit editing mode
}

export default function FloatingToolbarPlugin({
  anchorElem = document.body,
  editorShellRef,
  onExitEditingMode,
}: FloatingToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  // const [activeEditor, setActiveEditor] = useState(editor); // Removed: Use editor from context directly

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false); // Added isCode state
  const [isLink, setIsLink] = useState(false);
  const [isUL, setIsUL] = useState(false);
  const [isOL, setIsOL] = useState(false);
  const [currentBlockType, setCurrentBlockType] = useState<string | null>(null);
  const [currentTextAlign, setCurrentTextAlign] = useState<'left' | 'center' | 'right' | 'justify'>('left');
  const [currentFontSize, setCurrentFontSize] = useState<string>(DEFAULT_FONT_SIZE);
  const [currentFontColor, setCurrentFontColor] = useState<string>(DEFAULT_FONT_COLOR);
  // canUndo, canRedo state removed
  const [isEditorFocused, setIsEditorFocused] = useState(editor.getRootElement() === document.activeElement);


  const updateToolbar = useCallback(() => {
    editor.read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const commonAnchorNode = selection.anchor.getNode();

        // Update text format states (font size, color)
        const nodeForStyleCheck = $isTextNode(commonAnchorNode)
          ? commonAnchorNode
          : ($isTextNode(commonAnchorNode.getPreviousSibling())
            ? commonAnchorNode.getPreviousSibling()
            : ($isTextNode(commonAnchorNode.getParent()?.getFirstChild())
              ? commonAnchorNode.getParent()!.getFirstChild()
              : null));

        if (nodeForStyleCheck && $isTextNode(nodeForStyleCheck)) {
          const style = nodeForStyleCheck.getStyle();
          const parsedStyle = parseStyleText(style);
          const newFontSize = parsedStyle['font-size'] || DEFAULT_FONT_SIZE;
          if (newFontSize !== currentFontSize) setCurrentFontSize(newFontSize);
          const newFontColor = parsedStyle['color'] || DEFAULT_FONT_COLOR;
          if (newFontColor !== currentFontColor) setCurrentFontColor(newFontColor);
        } else {
          if (DEFAULT_FONT_SIZE !== currentFontSize) setCurrentFontSize(DEFAULT_FONT_SIZE);
          if (DEFAULT_FONT_COLOR !== currentFontColor) setCurrentFontColor(DEFAULT_FONT_COLOR);
        }

        const newIsBold = selection.hasFormat('bold');
        if (newIsBold !== isBold) setIsBold(newIsBold);
        const newIsItalic = selection.hasFormat('italic');
        if (newIsItalic !== isItalic) setIsItalic(newIsItalic);
        const newIsUnderline = selection.hasFormat('underline');
        if (newIsUnderline !== isUnderline) setIsUnderline(newIsUnderline);
        const newIsStrikethrough = selection.hasFormat('strikethrough');
        if (newIsStrikethrough !== isStrikethrough) setIsStrikethrough(newIsStrikethrough);
        const newIsCode = selection.hasFormat('code'); // Check for code format
        if (newIsCode !== isCode) setIsCode(newIsCode); // Update isCode state

        // Update link state
        const selectedNodeForLink = getSelectedNode(selection);
        const parentOfSelectedForLink = selectedNodeForLink.getParent();
        const newIsLink = $isLinkNode(parentOfSelectedForLink) || $isLinkNode(selectedNodeForLink);
        if (newIsLink !== isLink) setIsLink(newIsLink);

        // Update list state
        const element = commonAnchorNode.getKey() === 'root' ? commonAnchorNode : $getNearestNodeOfType(commonAnchorNode, ListNode);
        if (element && $isListNode(element)) {
          const listType = element.getListType();
          if (listType !== currentBlockType) setCurrentBlockType(listType);
          const newIsUL = listType === 'bullet';
          if (newIsUL !== isUL) setIsUL(newIsUL);
          const newIsOL = listType === 'number';
          if (newIsOL !== isOL) setIsOL(newIsOL);
        } else {
          if (null !== currentBlockType) setCurrentBlockType(null);
          if (false !== isUL) setIsUL(false);
          if (false !== isOL) setIsOL(false);
        }

        // Update text align state
        const elementFormatNode = $getNearestNodeOfType(commonAnchorNode, ElementNode);
        if (elementFormatNode) {
          const formatType = elementFormatNode.getFormatType() as 'left' | 'center' | 'right' | 'justify';
          if (formatType !== currentTextAlign) setCurrentTextAlign(formatType);
        } else {
          if ('left' !== currentTextAlign) setCurrentTextAlign('left');
        }
      }
    });

    // Position toolbar below the editor shell - only update position if editor is focused
    if (toolbarRef.current && editorShellRef.current && isEditorFocused) {
      const shellRect = editorShellRef.current.getBoundingClientRect();
      const scrollY = window.scrollY; // Account for page scroll
      
      // Calculate position once
      const top = `${shellRect.bottom + scrollY + 8}px`; // Adjusted to 8px
      const left = `${shellRect.left + (shellRect.width / 2) - (toolbarRef.current.offsetWidth / 2)}px`;
      
      // Only update if position has changed significantly
      if (toolbarRef.current.style.top !== top || 
          Math.abs(parseFloat(toolbarRef.current.style.left || '0') - parseFloat(left)) > 5) {
        
        toolbarRef.current.style.opacity = '1';
        toolbarRef.current.style.top = top;
        toolbarRef.current.style.left = left;
        
        // Ensure toolbar stays within viewport horizontally
        const toolbarRect = toolbarRef.current.getBoundingClientRect();
        if (toolbarRect.right > window.innerWidth) {
          toolbarRef.current.style.left = `${window.innerWidth - toolbarRect.width - 5}px`;
        }
        if (toolbarRect.left < 0) {
          toolbarRef.current.style.left = '5px';
        }
      }
    }

  }, [
    editor, editorShellRef, 
    isBold, isItalic, isUnderline, isStrikethrough, isCode, isLink,
    isUL, isOL, currentBlockType, currentTextAlign,
    currentFontSize, currentFontColor, isEditorFocused // Added isEditorFocused
  ]);

  // Callback for handling blur, memoized
  const handleBlur = useCallback(() => {
    // Use setTimeout to allow focus to shift before checking activeElement.
    window.setTimeout(() => {
      const activeEl = document.activeElement;
      const isFocusInsideEditorShell = editorShellRef.current?.contains(activeEl as Node);
      const isFocusInsideToolbar = toolbarRef.current?.contains(activeEl as Node);

      if (!isFocusInsideEditorShell && !isFocusInsideToolbar) {
        setIsEditorFocused(false);
        if (onExitEditingMode) {
          onExitEditingMode();
        }
      }
    }, 0);
  }, [editorShellRef, toolbarRef, onExitEditingMode, setIsEditorFocused]);


  // Main useEffect for editor commands and event listeners
  useEffect(() => {
    const editorElement = editor.getRootElement();

    const unregisterFocus = editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        setIsEditorFocused(true);
        return false; // Let other focus handlers run
      },
      LowPriority,
    );

    const unregisterSelectionChange = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar(); // updateToolbar itself depends on isEditorFocused for positioning
        return false;
      },
      LowPriority,
    );
    
    if (editorElement) {
      editorElement.addEventListener('blur', handleBlur);
    }

    return () => {
      unregisterFocus();
      unregisterSelectionChange();
      if (editorElement) {
        editorElement.removeEventListener('blur', handleBlur);
      }
    };
  }, [editor, updateToolbar, handleBlur, setIsEditorFocused]);

  // Effect to react to isEditorFocused changes (e.g., show/hide toolbar, update position)
  useEffect(() => {
    if (isEditorFocused) {
      // When editor becomes focused, ensure toolbar is updated and visible.
      updateToolbar(); // Recalculates states and positions
      if (toolbarRef.current) {
        toolbarRef.current.style.opacity = '1';
      }
    } else {
      // When editor loses focus (and focus isn't on toolbar), hide toolbar.
      if (toolbarRef.current) {
        toolbarRef.current.style.opacity = '0';
      }
    }
  }, [isEditorFocused, updateToolbar]);

  // Add a debounced update for toolbar position to prevent flickering during typing
  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout;
    
    const handleSelectionChange = () => {
      // Clear any existing timeout
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      
      // Set a new timeout to update toolbar position after typing stops
      debounceTimeout = setTimeout(() => {
        if (isEditorFocused) {
          updateToolbar();
        }
      }, 100); // 100ms debounce
    };
    
    // Register the selection change handler
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          handleSelectionChange();
        }
      });
    });
    
    return () => {
      // Clean up
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      unregister();
    };
  }, [editor, isEditorFocused, updateToolbar]);


  const insertLink = useCallback(() => {
    editor.getEditorState().read(() => { // Changed activeEditor to editor
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const url = prompt('Enter link URL:'); // Simple prompt, consider a modal for better UX
        if (url) {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, url); // Changed activeEditor to editor
        }
      }
    });
  }, [editor]); // Changed activeEditor to editor

  const formatBulletList = () => {
    if (currentBlockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined); // Changed activeEditor to editor
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined); // Changed activeEditor to editor
    }
  };

  const formatNumberedList = () => {
    if (currentBlockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined); // Changed activeEditor to editor
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined); // Changed activeEditor to editor
    }
  };

  const formatAlignment = (alignType: 'left' | 'center' | 'right' | 'justify') => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignType); // Changed activeEditor to editor
  };
  
  // Toolbar should only be visible if the editor is editable (i.e., isEditing is true in TextNode)
  // The parent component LexicalEditorComponent already conditionally renders this plugin based on isEditing.
  // So, we don't need an additional !editor.isEditable() check here.

  const applyStyleText = useCallback(
    (stylesToApply: Record<string, string>) => {
      editor.update(() => { // Changed activeEditor to editor
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const nodes = selection.getNodes();
          nodes.forEach(node => {
            if ($isTextNode(node)) {
              const existingStyleObj = parseStyleText(node.getStyle());
              const newStyleObj = { ...existingStyleObj, ...stylesToApply };
              // Filter out undefined properties, which can happen if a style is being "unset" to default
              for (const key in newStyleObj) {
                if (newStyleObj[key] === undefined) {
                  delete newStyleObj[key];
                }
              }
              node.setStyle(stringifyStyle(newStyleObj));
            }
          });
        }
      });
    },
    [editor], // Changed activeEditor to editor
  );

  const handleFontColorChange = (color: string) => {
    applyStyleText({ color });
  };


  return createPortal(
    <div
      ref={toolbarRef}
      className="lexical-toolbar absolute bg-white dark:bg-gray-700 shadow-xl rounded-md p-1 flex flex-wrap items-center z-[1001] transition-opacity duration-150 border border-gray-200 dark:border-gray-600" // Added flex-wrap
      style={{ opacity: 0 }} // Initially hidden, shown by updateToolbar
    >
      {/* Undo and Redo buttons removed */}
      {/* Font Size Buttons */}
      <div className="flex items-center mx-1">
        <Type size={16} className="mr-1 text-gray-700 dark:text-gray-300" />
        <div className="relative inline-block text-left group">
          <button
            title="Font Size"
            onMouseDown={(e) => e.preventDefault()}
            className="p-2 m-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          >
            {currentFontSize.replace('px', '')}
          </button>
          <div className="absolute left-0 w-auto p-1 bg-white dark:bg-gray-700 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 invisible group-hover:visible flex flex-wrap z-10 border border-gray-200 dark:border-gray-600">
            {FONT_SIZES.map((size) => (
              <button
                key={size}
                title={size}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setCurrentFontSize(size);
                  applyStyleText({ 'font-size': size });
                }}
                className={`px-3 py-1 m-0.5 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
                  currentFontSize === size ? 'bg-blue-100 text-blue-600 dark:bg-blue-800 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {size.replace('px', '')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Font Color Picker */}
      <div className="relative inline-block text-left mx-1 group">
        <ToolbarButton title="Text Color">
          <div
            className="w-[18px] h-[18px] rounded-full border border-gray-300 dark:border-gray-600"
            style={{ backgroundColor: currentFontColor }}
            aria-label={`Current color: ${currentFontColor}`}
          />
        </ToolbarButton>
        <div className="absolute left-0 p-1 bg-white dark:bg-gray-700 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 invisible group-hover:visible grid grid-cols-3 gap-1 z-10 border border-gray-200 dark:border-gray-600" style={{width: 'max-content'}}>
          {FONT_COLORS.map((color) => (
            <button
              key={color}
              title={color}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFontColorChange(color)}
              className="w-6 h-6 rounded-sm border border-gray-300 dark:border-gray-600 hover:ring-2 hover:ring-blue-500 dark:hover:ring-blue-400"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>
      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      <ToolbarButton
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')} // Changed activeEditor to editor
        isActive={isBold}
        title="Bold (Ctrl+B)"
      >
        <Bold size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')} // Changed activeEditor to editor
        isActive={isItalic}
        title="Italic (Ctrl+I)"
      >
        <Italic size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')} // Changed activeEditor to editor
        isActive={isUnderline}
        title="Underline (Ctrl+U)"
      >
        <Underline size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')} // Changed activeEditor to editor
        isActive={isStrikethrough}
        title="Strikethrough"
      >
        <Strikethrough size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
        isActive={isCode}
        title="Code Block"
      >
        <Code size={18} />
      </ToolbarButton>
      <ToolbarButton onClick={insertLink} isActive={isLink} title="Insert Link">
        <Link size={18} />
      </ToolbarButton>
      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>
      <ToolbarButton onClick={formatBulletList} isActive={isUL} title="Bullet List">
        <List size={18} />
      </ToolbarButton>
      <ToolbarButton onClick={formatNumberedList} isActive={isOL} title="Numbered List">
        <ListOrdered size={18} />
      </ToolbarButton>
      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>
      <ToolbarButton onClick={() => formatAlignment('left')} isActive={currentTextAlign === 'left'} title="Align Left">
        <AlignLeft size={18} />
      </ToolbarButton>
      <ToolbarButton onClick={() => formatAlignment('center')} isActive={currentTextAlign === 'center'} title="Align Center">
        <AlignCenter size={18} />
      </ToolbarButton>
      <ToolbarButton onClick={() => formatAlignment('right')} isActive={currentTextAlign === 'right'} title="Align Right">
        <AlignRight size={18} />
      </ToolbarButton>
      <ToolbarButton onClick={() => formatAlignment('justify')} isActive={currentTextAlign === 'justify'} title="Align Justify">
        <AlignJustify size={18} />
      </ToolbarButton>
    </div>,
    anchorElem
  );
}
